import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx'
import { copyWithToast } from '../../utils/clipboard'

interface DraftSummaryDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

const DraftSummaryDisplay: React.FC<DraftSummaryDisplayProps> = ({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const [showRawData, setShowRawData] = useState(false)
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = (text: string) => copyWithToast(text, showToast);

  // Parse JSON if result is a string
  let parsedResult = result
  if (typeof result === 'string') {
    try {
      parsedResult = JSON.parse(result)
    } catch (e) {
      // If parsing fails, return null for self-detection
      return null;
    }
  }

  // Handle both direct result and nested output structure
  const summary = parsedResult?.output || parsedResult

  // Paragraph mode detection
  const isParagraphMode = summary && typeof summary === 'object' && (
    summary.output_format === 'paragraphs' || !!summary.summary_text
  );

  // Self-detection: check if this looks like a draft summary
  const isDraftSummary = isParagraphMode || (summary && typeof summary === 'object' && (
    summary.invention_overview ||
    summary.technical_solution ||
    summary.key_advantages ||
    summary.preferred_embodiments ||
    summary.alternative_embodiments ||
    summary.brief_description ||
    summary.problem_solved
  ));

  if (!isDraftSummary) {
    return null; // Not a draft summary, let other components handle it
  }

  if (!summary || typeof summary !== 'object') {
    return null;
  }

  // ---- Paragraph mode render ----
  if (isParagraphMode) {
    const summaryText: string = summary.summary_text || '';

    const exportParagraphsToDocx = async () => {
      try {
        const paras = summaryText.split('\n\n').filter((p: string) => p.trim());
        const docChildren: any[] = paras.map((para: string) => {
          const trimmed = para.trim();
          const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
          if (isHeading) {
            return new Paragraph({ text: trimmed, heading: HeadingLevel.HEADING_2, spacing: { before: 300, after: 200 } });
          }
          return new Paragraph({ text: trimmed, spacing: { after: 200, line: 360 } });
        });
        const doc = new Document({ sections: [{ properties: {}, children: docChildren }] });
        const blob = await Packer.toBlob(doc);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'draft-summary.docx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showToast?.('Draft Summary exported to Word document');
      } catch (error) {
        console.error('Error exporting paragraphs to DOCX:', error);
        showToast?.('Error exporting Draft Summary');
      }
    };

    const renderParagraphs = () =>
      summaryText.split('\n\n').filter((p: string) => p.trim()).map((para: string, idx: number) => {
        const trimmed = para.trim();
        const isHeading = trimmed === trimmed.toUpperCase() && trimmed.length < 80 && /[A-Z]/.test(trimmed);
        if (isHeading) {
          return <p key={idx} className={`font-semibold mt-4 mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{trimmed}</p>;
        }
        return <p key={idx} className={`leading-relaxed mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{trimmed}</p>;
      });

    return (
      <div className="space-y-4">
        {showHeader && (
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Draft Summary</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full paragraph format</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={exportParagraphsToDocx} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
                <Download className="w-4 h-4" />Export DOCX
              </button>
              <button onClick={() => copyToClipboard(summaryText)} className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                <Copy className="w-4 h-4" />Copy All
              </button>
            </div>
          </div>
        )}
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
          {summaryText ? renderParagraphs() : (
            <p className={`text-sm italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No summary text available.</p>
          )}
        </div>
      </div>
    );
  }
  // ---- End paragraph mode render ----

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    invention_overview: { 
      title: 'Invention Overview', 
      emoji: '🎯', 
      type: 'text',
      description: 'High-level description of the invention'
    },
    technical_solution: { 
      title: 'Technical Solution', 
      emoji: '⚙️', 
      type: 'text',
      description: 'Core technical approach and methodology'
    },
    key_advantages: { 
      title: 'Key Advantages', 
      emoji: '✨', 
      type: 'array',
      description: 'Primary benefits and improvements'
    },
    preferred_embodiments: { 
      title: 'Preferred Embodiments', 
      emoji: '🏅', 
      type: 'array',
      description: 'Primary implementations of the invention'
    },
    alternative_embodiments: { 
      title: 'Alternative Embodiments', 
      emoji: '🔄', 
      type: 'array',
      description: 'Alternative implementations and variations'
    },
    applications: { 
      title: 'Applications', 
      emoji: '🏭', 
      type: 'array',
      description: 'Fields and industries where invention applies'
    },
    technical_effects: { 
      title: 'Technical Effects', 
      emoji: '🔧', 
      type: 'array',
      description: 'Technical impacts and benefits achieved'
    },
    scope_protection: { 
      title: 'Scope of Protection', 
      emoji: '🛡️', 
      type: 'text',
      description: 'Legal protection scope and claims coverage'
    },
    technical_features: { 
      title: 'Technical Features', 
      emoji: '🔧', 
      type: 'array',
      description: 'Main technical characteristics'
    },
    problem_solved: { 
      title: 'Problem Solved', 
      emoji: '🎯', 
      type: 'text',
      description: 'The primary problem addressed'
    },
    application_areas: { 
      title: 'Application Areas', 
      emoji: '🏭', 
      type: 'array',
      description: 'Fields and industries where invention applies'
    },
    objectives_achieved: { 
      title: 'Objectives Achieved', 
      emoji: '🏆', 
      type: 'array',
      description: 'Goals accomplished by the invention'
    },
    brief_description: { 
      title: 'Brief Description', 
      emoji: '📝', 
      type: 'text',
      description: 'Concise summary of the invention'
    }
  }

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Draft Summary",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const value = summary[sectionKey];

        // Skip empty sections
        if (!value || (Array.isArray(value) && value.length === 0)) {
          return;
        }

        // Section heading
        children.push(
          new Paragraph({
            text: config.title,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );

        if (config.type === 'text' && value) {
          children.push(
            new Paragraph({
              text: value,
              spacing: { after: 200, line: 360 },
            })
          );
        } else if (config.type === 'array' && Array.isArray(value) && value.length > 0) {
          value.forEach((item, index) => {
            const itemText = typeof item === 'object' ? JSON.stringify(item) : String(item);
            children.push(
              new Paragraph({
                text: `• ${itemText}`,
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          });
        }
      });

      // Check if we have content
      const hasContent = Object.entries(sectionConfigs).some(([sectionKey, config]) => {
        const value = summary[sectionKey];
        return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0));
      });

      if (!hasContent) {
        children.push(
          new Paragraph({
            text: "No summary content available for export",
            italics: true,
            spacing: { before: 200 },
          })
        );
      }

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'draft-summary.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Draft Summary exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Draft Summary to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Draft Summary');
      }
    }
  }

  const formatFieldValue = (field, value, config) => {
    if (!value) return 'Not specified'
    
    if (config.type === 'array') {
      if (!Array.isArray(value) || value.length === 0) return 'None specified'
      return (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="leading-relaxed">
              {typeof item === 'object' ? JSON.stringify(item) : item}
            </li>
          ))}
        </ul>
      )
    }
    
    if (config.type === 'text') {
      return <div className="leading-relaxed">{value}</div>
    }
    
    return value
  }

  const renderSection = (sectionKey, config) => {
    const value = summary[sectionKey]
    const hasData = value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
    
    if (!hasData) return null

    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {config.description}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className={`text-sm font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    {config.title}
                  </span>
                  <button
                    onClick={() => copyToClipboard(config.type === 'array' && Array.isArray(value) ? value.join('\n') : value.toString())}
                    className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  >
                    <Copy className="w-3 h-3" />
                    Copy Field
                  </button>
                </div>
                <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {formatFieldValue(sectionKey, value, config)}
                </div>
              </div>
            </div>

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => copyToClipboard(`${config.title}: ${config.type === 'array' && Array.isArray(value) ? value.join('\n- ') : value}`)}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
              {addToResources && (
                <button
                  onClick={() => {
                    const sectionText = `# ${config.title}\n\n${config.type === 'array' && Array.isArray(value) ? '- ' + value.join('\n- ') : value}`
                    addToResources({
                      type: 'summary_section',
                      title: config.title,
                      content: sectionText,
                      source: 'Draft Summary Task'
                    })
                    showToast?.('Section added to resources!')
                  }}
                  className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'}`}
                >
                  Add to Resources
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Get sections with data
  const sectionsWithData = Object.keys(sectionConfigs).filter(sectionKey => {
    const value = summary[sectionKey]
    const config = sectionConfigs[sectionKey]
    return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
  })
  
  const allSectionsText = Object.entries(sectionConfigs)
    .filter(([sectionKey, config]) => {
      const value = summary[sectionKey]
      return value && (config.type === 'text' ? value.trim() : (Array.isArray(value) && value.length > 0))
    })
    .map(([sectionKey, config]) => {
      const value = summary[sectionKey]
      const formattedValue = config.type === 'array' && Array.isArray(value) ? '- ' + value.join('\n- ') : value
      return `# ${config.title}\n\n${formattedValue}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Draft Summary
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {sectionsWithData.length} section{sectionsWithData.length !== 1 ? 's' : ''} with content
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-blue-700 hover:bg-blue-600 text-blue-100' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                title="Export summary as Word document"
              >
                <Download className="w-4 h-4" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
              <button
                onClick={() => setShowRawData(!showRawData)}
                className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                title="Toggle raw data display"
              >
                {showRawData ? 'Hide Raw' : 'Show Raw'}
              </button>
            </div>
          </div>

          {/* Raw Data Display */}
          {showRawData && (
            <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Raw Data
                </h3>
                <button
                  onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
                  className={`text-sm px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                >
                  <Copy className="w-3 h-3" />
                  Copy Raw Data
                </button>
              </div>
              <pre className={`text-xs overflow-auto max-h-96 p-3 rounded ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-800'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                sectionsWithData.forEach(sectionKey => {
                  allExpanded[sectionKey] = true
                })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                sectionsWithData.forEach(sectionKey => {
                  allCollapsed[sectionKey] = false
                })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {Object.entries(sectionConfigs).map(([sectionKey, config]) => renderSection(sectionKey, config))}

        {/* No content message */}
        {sectionsWithData.length === 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No summary sections contain data to display.
          </div>
        )}
      </div>
    </div>
  )
}

export default DraftSummaryDisplay